import { useState, useEffect } from 'react';
import { portalGetAbrechnung } from '../services/api';
import { FileText, CalendarBlank, Info, CurrencyEur } from '@phosphor-icons/react';

export default function TenantAbrechnung() {
  const [year, setYear] = useState(new Date().getFullYear() - 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await portalGetAbrechnung(year);
      setData(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Fehler beim Laden der Abrechnung');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount || 0);

  const verteilerLabel = (v) => {
    const labels = { flaeche: 'Wohnfläche', personen: 'Personen', einheiten: 'Einheiten', verbrauch: 'Verbrauch' };
    return labels[v] || v;
  };

  const years = [];
  const cy = new Date().getFullYear();
  for (let y = cy; y >= cy - 5; y--) years.push(y);

  return (
    <div className="space-y-6" data-testid="tenant-abrechnung-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-slate-900">
          Meine Nebenkostenabrechnung
        </h1>
        <p className="text-slate-500 mt-1 text-sm sm:text-base">Ihre Anteile an den umlagefähigen Betriebskosten</p>
      </div>

      <div className="bg-white border border-gray-200 p-4 flex items-center gap-3">
        <CalendarBlank weight="bold" className="w-5 h-5 text-slate-400" />
        <select
          value={year}
          onChange={(e) => setYear(parseInt(e.target.value))}
          className="border-b border-gray-300 bg-transparent py-1 focus:border-slate-900 outline-none font-mono font-bold text-lg"
          data-testid="tenant-year-select"
        >
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <span className="text-sm text-slate-500">Abrechnungsjahr wählen</span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-700"></div>
        </div>
      ) : error ? (
        <div className="bg-amber-50 border border-amber-200 p-6 text-amber-800" data-testid="tenant-abrechnung-error">
          {error}
        </div>
      ) : data && data.kostenstellen && data.kostenstellen.length > 0 ? (
        <>
          {/* Summary */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-emerald-50 border border-emerald-200 p-6 sm:col-span-3">
              <p className="text-xs uppercase tracking-widest font-bold text-emerald-700">Ihre Nebenkosten {year}</p>
              <p className="font-mono text-3xl sm:text-5xl font-black tracking-tighter text-emerald-700 mt-2" data-testid="tenant-total-cost">
                {formatCurrency(data.gesamt_nebenkosten)}
              </p>
              <p className="text-sm text-emerald-700 mt-2">
                Einheit: <strong>{data.unit?.name}</strong> · {data.unit?.flaeche || 0} m² · {data.unit?.personen || 0} Personen
              </p>
            </div>
          </div>

          {/* Verteilungsschlüssel */}
          <div className="bg-white border border-gray-200 p-6">
            <h2 className="font-heading text-lg font-bold text-slate-900 mb-4">Ihre Anteile</h2>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-xs uppercase tracking-widest font-bold text-slate-500">Nach Fläche</p>
                <p className="font-mono text-2xl font-black text-slate-900 mt-1">{data.anteile?.flaeche?.toFixed(2)}%</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest font-bold text-slate-500">Nach Personen</p>
                <p className="font-mono text-2xl font-black text-slate-900 mt-1">{data.anteile?.personen?.toFixed(2)}%</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest font-bold text-slate-500">Nach Einheiten</p>
                <p className="font-mono text-2xl font-black text-slate-900 mt-1">{data.anteile?.einheiten?.toFixed(2)}%</p>
              </div>
            </div>
          </div>

          {/* Kostenstellen Breakdown */}
          <div className="bg-white border border-gray-200">
            <div className="px-4 sm:px-6 py-4 bg-emerald-50 border-b border-emerald-200">
              <h2 className="font-heading text-lg font-bold text-emerald-800">Kostenaufstellung</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="tenant-abrechnung-table">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider font-bold text-slate-500">Kostenstelle</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider font-bold text-slate-500">Verteiler</th>
                    <th className="text-right py-3 px-4 text-xs uppercase tracking-wider font-bold text-slate-500">Anteil</th>
                    <th className="text-right py-3 px-4 text-xs uppercase tracking-wider font-bold text-slate-500">Gesamt</th>
                    <th className="text-right py-3 px-4 text-xs uppercase tracking-wider font-bold text-slate-500">Ihr Anteil</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.kostenstellen.map((ks, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="py-3 px-4 font-medium text-slate-900">{ks.name}</td>
                      <td className="py-3 px-4 text-sm text-slate-500">{verteilerLabel(ks.verteilerschluessel)}</td>
                      <td className="py-3 px-4 text-right font-mono text-sm text-slate-600">{ks.anteil_prozent?.toFixed(2)}%</td>
                      <td className="py-3 px-4 text-right font-mono text-slate-600">{formatCurrency(ks.gesamt_kosten)}</td>
                      <td className="py-3 px-4 text-right font-mono font-bold text-emerald-700">{formatCurrency(ks.einheit_kosten)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-100 font-bold">
                    <td colSpan={4} className="py-3 px-4 text-right">Gesamt:</td>
                    <td className="py-3 px-4 text-right font-mono text-emerald-700">{formatCurrency(data.gesamt_nebenkosten)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 p-4 text-sm text-blue-800 flex gap-3">
            <Info weight="bold" className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <p>Die Abrechnung erfolgt nach BetrKV §2. Verteilerschlüssel werden pro Kostenstelle vom Vermieter festgelegt. Bei Fragen wenden Sie sich bitte über die Nachrichten-Funktion an Ihren Vermieter.</p>
          </div>
        </>
      ) : (
        <div className="bg-white border border-gray-200 p-12 text-center">
          <FileText weight="light" className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">Keine Abrechnung verfügbar</h3>
          <p className="text-slate-500">Für {year} wurden noch keine Belege erfasst.</p>
        </div>
      )}
    </div>
  );
}
